import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, keyframes } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import api from '../../api/client';
import { useToast } from '../shared/Toast';
import ConfirmDialog from '../shared/ConfirmDialog';

const goldShimmer = keyframes`
  0% { top: -80%; opacity: 0; }
  15% { opacity: 0.8; }
  50% { top: 80%; opacity: 0.8; }
  65% { opacity: 0; }
  100% { top: 180%; opacity: 0; }
`;

const bellPulse = keyframes`
  0%, 100% { transform: scale(1); }
  25% { transform: scale(1.3) rotate(-10deg); }
  50% { transform: scale(1.3) rotate(10deg); }
  75% { transform: scale(1.2) rotate(-5deg); }
`;


const badgePulse = keyframes`
  0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(117,55,66,0.4); }
  50% { transform: scale(1.15); box-shadow: 0 0 0 6px rgba(117,55,66,0); }
`;

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
}

interface NavDotItem {
  label: string;
  path: string;
  colour: string;
}

const MAIN_NAV: NavItem[] = [
  {
    label: 'Dashboard', path: '/',
    icon: <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>,
  },
  {
    label: 'Company', path: '/company',
    icon: <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M3 21h18"/><path d="M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16"/><path d="M9 7h2M13 7h2M9 11h2M13 11h2M9 15h2M13 15h2"/></svg>,
  },
  {
    label: 'Contacts', path: '/contacts',
    icon: <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="9" cy="7" r="4"/><path d="M2 21v-2a4 4 0 0 1 4-4h6a4 4 0 0 1 4 4v2"/><path d="M19 8v6m-3-3h6"/></svg>,
  },
  {
    label: 'Pipeline', path: '/pipeline',
    icon: <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M3 3h18v4H3zM3 10h18v4H3zM3 17h18v4H3z"/></svg>,
  },
  {
    label: 'Demos', path: '/demos',
    icon: <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>,
  },
  {
    label: 'Tasks', path: '/tasks',
    icon: <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>,
  },
];

const REPORTS_NAV: NavItem[] = [
  {
    label: 'Metrics', path: '/metrics',
    icon: <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
  },
  {
    label: 'Email log', path: '/email-log',
    icon: <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>,
  },
];

const MARKETING_NAV: NavItem[] = [
  {
    label: 'Marketing', path: '/marketing',
    icon: <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M22 2L11 13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>,
  },
  {
    label: 'Unsubscribed', path: '/unsubscribed',
    icon: <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M2 7l10 7 10-7"/><path d="M4 4l16 16"/></svg>,
  },
  {
    label: 'Social', path: '/social',
    icon: <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>,
  },
  {
    label: 'Automation', path: '/automation',
    icon: <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>,
  },
];

const GDPR_NAV: NavItem[] = [
  {
    label: 'GDPR', path: '/gdpr',
    icon: <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  },
];

const HELP_NAV: NavItem[] = [
  {
    label: 'Requests & Bugs', path: '/requests',
    icon: <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 9v4"/><path d="M12 17h.01"/><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/></svg>,
  },
  {
    label: 'Platform Guide', path: '/platform-guide',
    icon: <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>,
  },
];

const SETTINGS_NAV: NavItem[] = [
  {
    label: 'Import contacts', path: '/import',
    icon: <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>,
  },
  {
    label: 'User management', path: '/team',
    icon: <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="9" cy="7" r="4"/><path d="M2 21v-2a4 4 0 0 1 4-4h6a4 4 0 0 1 4 4v2"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg>,
  },
  {
    label: 'Settings', path: '/settings',
    icon: <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>,
  },
];

interface SidebarProps {
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

export default function Sidebar({ mobileOpen = false, onMobileClose }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Wrap navigate so mobile drawer closes after a nav tap
  const navigateAndClose = (path: string) => {
    navigate(path);
    onMobileClose?.();
  };

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const queryClient = useQueryClient();
  const [notifOpen, setNotifOpen] = useState(false);
  const notifWrapperRef = useRef<HTMLDivElement>(null);

  // Close notification dropdown on outside click
  useEffect(() => {
    if (!notifOpen) return;
    const handler = (e: MouseEvent) => {
      if (notifWrapperRef.current && !notifWrapperRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [notifOpen]);

  const { showToast } = useToast();
  const prevUnreadRef = useRef<number>(0);
  const hasInitializedRef = useRef<boolean>(false);
  const [bellAnimating, setBellAnimating] = useState(false);
  const [sidebarGlow, setSidebarGlow] = useState(false);
  // VR-52. Clear all empties the whole panel, so it asks first.
  const [confirmClearAll, setConfirmClearAll] = useState(false);
  const [clearing, setClearing] = useState(false);

  // Poll notifications every 10 seconds
  const { data: notifData, isFetched } = useQuery({
    queryKey: ['notifications'],
    // `total` is everything in the panel; `notifications` is the first
    // twenty of it. VR-52 needs the former for the Clear all count.
    queryFn: async () => { const { data } = await api.get('/notifications'); return data.data as { notifications: any[]; unreadCount: number; total: number }; },
    staleTime: 10_000,
    refetchInterval: 30_000, // fallback; real-time push (SSE) handles the rest
  });

  const unreadCount = notifData?.unreadCount ?? 0;

  // Claim / release / resolve an actionable notification.
  const notifAction = async (id: string, action: 'claim' | 'release' | 'resolve') => {
    try {
      await api.post(`/notifications/${id}/${action}`);
    } catch (e: any) {
      showToast(e?.response?.data?.error?.message ?? 'Action failed', 'error');
    } finally {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    }
  };
  const notifBadge = { display: 'inline-block', p: '2px 8px', borderRadius: '999px', fontSize: '10px', fontWeight: 700 } as const;
  const notifActBtn = { p: '3px 8px', borderRadius: '5px', fontSize: '10px', fontWeight: 700, cursor: 'pointer', border: '1.5px solid #e0dbd4', bgcolor: '#fff', color: '#003554', fontFamily: 'inherit' } as const;

  // Real-time: refresh the instant anything changes (Server-Sent Events).
  // The 30s poll above is the fallback if the stream drops. EventSource
  // authenticates via the same cookie the app already uses, and auto-reconnects.
  useEffect(() => {
    const es = new EventSource('/api/notifications/stream');
    es.onmessage = () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      // VR-33. The notifications PAGE keeps its own cache and was never told
      // anything had changed, so it sat on a 60-second poll and looked like
      // it needed a hard refresh. The stream is already open here; one extra
      // line is better than a second connection per user.
      queryClient.invalidateQueries({ queryKey: ['notifications-page'] });
      queryClient.invalidateQueries({ queryKey: ['notification'] });
    };
    return () => es.close();
  }, [queryClient]);

  // Show toast popups for NEW notifications as they arrive.
  // Skip the very first fetch (initial page load) to avoid firing banners
  // for notifications that were already there when the user logged in, but
  // fire on the first 0→N transition after that (critical for first-inbound).
  useEffect(() => {
    if (!isFetched) return;
    if (hasInitializedRef.current && unreadCount > prevUnreadRef.current) {
      // New notifications arrived — find the new ones
      const newNotifs = notifData?.notifications?.filter((n: any) => !n.read).slice(0, unreadCount - prevUnreadRef.current) ?? [];
      // Dispatch event for AppLayout to show overlay banners
      if (newNotifs.length > 0) {
        window.dispatchEvent(new CustomEvent('mspdw:new-notifications', { detail: { notifications: newNotifs } }));
      }
      // Animate the bell + gold sidebar shimmer
      setBellAnimating(true);
      setSidebarGlow(true);
      setTimeout(() => setBellAnimating(false), 1000);
      setTimeout(() => setSidebarGlow(false), 7000);
    }
    prevUnreadRef.current = unreadCount;
    hasInitializedRef.current = true;
  }, [unreadCount, isFetched]);

  return (
    <>
      {/* Backdrop — mobile only, tap to close drawer */}
      <Box
        onClick={onMobileClose}
        sx={{
          display: { xs: mobileOpen ? 'block' : 'none', md: 'none' },
          position: 'fixed', inset: 0, bgcolor: 'rgba(0,0,0,0.4)', zIndex: 1200,
          transition: 'opacity 0.2s ease',
        }}
      />
      <Box sx={{
        width: 210, flexShrink: 0, bgcolor: '#003554', display: 'flex', flexDirection: 'column', height: '100vh',
        // Desktop: part of the flex layout. Mobile: fixed overlay drawer that slides in/out.
        position: { xs: 'fixed', md: 'relative' },
        top: 0, left: 0,
        zIndex: { xs: 1300, md: 'auto' },
        transform: { xs: mobileOpen ? 'translateX(0)' : 'translateX(-100%)', md: 'none' },
        transition: 'transform 0.25s ease',
        boxShadow: { xs: mobileOpen ? '4px 0 20px rgba(0,0,0,0.3)' : 'none', md: 'none' },
      }}>
      {/* Gold shimmer overlay — clipped inside its own container so it doesn't bleed out of the sidebar */}
      {sidebarGlow && (
        <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 10 }}>
          <Box sx={{
            position: 'absolute', left: 0, right: 0, height: '40%',
            background: 'linear-gradient(180deg, transparent 0%, rgba(251,191,36,0.15) 30%, rgba(251,191,36,0.35) 50%, rgba(251,191,36,0.15) 70%, transparent 100%)',
            animation: `${goldShimmer} 3s ease-in-out 2`,
            filter: 'blur(8px)',
          }} />
        </Box>
      )}
      {/* Logo */}
      <Box sx={{ p: '16px 16px 12px', borderBottom: '1px solid #00263d' }}>
        <Box component="img" src="/logo-white.png" alt="MSP Dark Web" sx={{ width: '80%', display: 'block' }} />
        <Typography sx={{ fontSize: '10px', color: '#61A0AF', mt: '4px' }}>CRM v2.0</Typography>
      </Box>

      {/* Main Nav */}
      <NavSection label="Main" />
      {MAIN_NAV.map((item) => (
        <NavItemRow key={item.path} label={item.label} icon={item.icon} active={isActive(item.path)} onClick={() => navigateAndClose(item.path)} />
      ))}

      {/* Reports */}
      <NavSection label="Reports" />
      {REPORTS_NAV.map((item) => (
        <NavItemRow key={item.path} label={item.label} icon={item.icon} active={isActive(item.path)} onClick={() => navigateAndClose(item.path)} />
      ))}

      {/* Marketing */}
      <NavSection label="Marketing" />
      {MARKETING_NAV.map((item) => (
        <NavItemRow key={item.path} label={item.label} icon={item.icon} active={isActive(item.path)} onClick={() => navigateAndClose(item.path)} />
      ))}

      {/* GDPR — admin-only, so we hide it for non-admins to avoid a confusing
          dead link. The server route is also admin-gated via requireAdmin. */}
      {user?.role === 'admin' && (
        <>
          <NavSection label="Compliance" />
          {GDPR_NAV.map((item) => (
            <NavItemRow key={item.path} label={item.label} icon={item.icon} active={isActive(item.path)} onClick={() => navigateAndClose(item.path)} />
          ))}
        </>
      )}

      {/* Help — available to all users */}
      <NavSection label="Help" />
      {HELP_NAV.map((item) => (
        <NavItemRow key={item.path} label={item.label} icon={item.icon} active={isActive(item.path)} onClick={() => navigateAndClose(item.path)} />
      ))}

      {/* Settings */}
      <NavSection label="Settings" />
      {SETTINGS_NAV.map((item) => (
        <NavItemRow key={item.path} label={item.label} icon={item.icon} active={isActive(item.path)} onClick={() => navigateAndClose(item.path)} />
      ))}

      {/* Bottom section — notifications + user */}
      <Box sx={{ mt: 'auto' }} />

      {/* Notifications bell */}
      <Box ref={notifWrapperRef} sx={{ px: '12px', pt: '4px', position: 'relative' }}>
        <Box onClick={() => setNotifOpen(!notifOpen)} sx={{
          display: 'flex', alignItems: 'center', gap: 1, p: '6px 10px', borderRadius: '6px', cursor: 'pointer',
          bgcolor: notifOpen ? '#00263d' : 'transparent', '&:hover': { bgcolor: '#00263d' },
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', animation: bellAnimating ? `${bellPulse} 0.6s ease` : 'none' }}>
            <svg width="14" height="14" fill="none" stroke={unreadCount > 0 ? '#fbbf24' : '#a8c8d0'} strokeWidth="2" viewBox="0 0 24 24"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
          </Box>
          <Typography sx={{ fontSize: '12px', color: unreadCount > 0 ? '#fbbf24' : '#a8c8d0', flex: 1, fontWeight: unreadCount > 0 ? 700 : 400 }}>Notifications</Typography>
          {unreadCount > 0 && (
            <Box sx={{ bgcolor: '#753742', color: '#fff', fontSize: '10px', fontWeight: 700, px: '5px', py: '1px', borderRadius: '10px', minWidth: 16, textAlign: 'center', animation: `${badgePulse} 2s ease-in-out infinite` }}>{unreadCount}</Box>
          )}
        </Box>
        {/* Notification dropdown */}
        {notifOpen && (
          <Box sx={{
            position: 'absolute', bottom: '100%', left: 12, width: 320, mb: '4px',
            bgcolor: '#fff', border: '1.5px solid #e0dbd4', borderRadius: '10px', boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            maxHeight: 400, overflowY: 'auto', zIndex: 1000,
          }}>
            <Box sx={{ p: '10px 14px', borderBottom: '1px solid #edeae4', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography onClick={() => { setNotifOpen(false); navigate('/notifications'); }}
                title="Open the notifications page"
                sx={{ fontSize: '13px', fontWeight: 700, color: '#003554', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>Notifications</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {unreadCount > 0 && (
                  <Box component="button" onClick={async (e: any) => {
                    e.stopPropagation();
                    await api.post('/notifications/read-all');
                    queryClient.invalidateQueries({ queryKey: ['notifications'] });
                  }} sx={{ fontSize: '11px', color: '#61A0AF', bgcolor: 'transparent', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}>Mark all read</Box>
                )}
                {(notifData?.notifications?.length ?? 0) > 0 && (
                  <Box component="button" onClick={(e: any) => { e.stopPropagation(); setConfirmClearAll(true); }}
                    sx={{ fontSize: '11px', color: '#753742', bgcolor: 'transparent', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}>Clear all</Box>
                )}
              </Box>
            </Box>
            {!notifData?.notifications?.length ? (
              <Box sx={{ p: '20px 14px', textAlign: 'center' }}>
                <Typography sx={{ fontSize: '12px', color: '#7a7060' }}>No notifications yet</Typography>
              </Box>
            ) : (
              notifData.notifications.slice(0, 15).map((n: any) => (
                <Box key={n.id} sx={{ display: 'flex', alignItems: 'flex-start', p: '10px 14px', borderBottom: '1px solid #f5f2ee', bgcolor: n.read ? 'transparent' : '#f5f2ee', '&:hover': { bgcolor: '#edeae4' } }}>
                  {/* Opens the notification, not the record it points at. The
                      dropdown truncates to one line and closes behind you, so
                      jumping straight to a contact meant the alert's own words —
                      which address went unmatched, which rule was stopped — were
                      gone before anybody had read them. The page shows it in
                      full, with "Open the record" one click away. */}
                  <Box onClick={() => { setNotifOpen(false); navigate(`/notifications/${n.id}`); }}
                    sx={{ flex: 1, cursor: 'pointer' }}>
                    <Typography sx={{ fontSize: '12px', fontWeight: n.read ? 400 : 700, color: '#003554' }}>{n.title}</Typography>
                    <Typography sx={{ fontSize: '11px', color: '#7a7060', mt: '2px' }}>{n.message}</Typography>
                    <Typography sx={{ fontSize: '10px', color: '#e0dbd4', mt: '2px' }}>{new Date(n.createdAt).toLocaleString()}</Typography>
                    {n.actionable && n.status && n.status !== 'info' && (
                      <Box onClick={(e: any) => e.stopPropagation()} sx={{ mt: '6px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        {n.status === 'resolved' ? (
                          <Box sx={{ ...notifBadge, bgcolor: '#dcfce7', color: '#15803d' }}>✓ Done{n.resolvedBy?.name ? ` · ${n.resolvedBy.name}` : ''}</Box>
                        ) : n.status === 'claimed' ? (
                          <>
                            <Box sx={{ ...notifBadge, bgcolor: '#dceef1', color: '#003554' }}>✋ {n.claimedByMe ? 'You’re handling this' : `${n.claimedBy?.name ?? 'Someone'} is handling`}</Box>
                            {n.claimedByMe && <Box component="button" onClick={() => notifAction(n.id, 'resolve')} sx={notifActBtn}>Mark done</Box>}
                            {n.claimedByMe && <Box component="button" onClick={() => notifAction(n.id, 'release')} sx={notifActBtn}>Release</Box>}
                          </>
                        ) : (
                          <>
                            <Box sx={{ ...notifBadge, bgcolor: '#fef3c7', color: '#92400e' }}>● Needs action</Box>
                            <Box component="button" onClick={() => notifAction(n.id, 'claim')} sx={{ ...notifActBtn, bgcolor: '#003554', color: '#fff', borderColor: '#003554' }}>✋ I’ll handle this</Box>
                          </>
                        )}
                      </Box>
                    )}
                  </Box>
                  {/* VR-52. The failure used to be silent: a .catch that threw
                      the error away, on a button that was not removing anything
                      in the first place. If it fails now, it says so. */}
                  <Box component="button" title="Clear this for me" onClick={async (e: any) => {
                    e.stopPropagation();
                    try {
                      await api.delete(`/notifications/${n.id}`);
                    } catch (err: any) {
                      showToast(err?.response?.data?.error?.message ?? 'Could not clear that one', 'error');
                    } finally {
                      queryClient.invalidateQueries({ queryKey: ['notifications'] });
                      queryClient.invalidateQueries({ queryKey: ['notifications-page'] });
                    }
                  }} sx={{ flexShrink: 0, mt: '2px', ml: '6px', p: '2px 4px', bgcolor: 'transparent', border: 'none', cursor: 'pointer', color: '#ccc', fontSize: '14px', lineHeight: 1, '&:hover': { color: '#753742' } }}>×</Box>
                </Box>
              ))
            )}
          </Box>
        )}
      </Box>

      {/* VR-52. Says the real number — the panel holds more than it shows. */}
      <ConfirmDialog
        open={confirmClearAll}
        title={`Clear ${notifData?.total ?? 0} notification${(notifData?.total ?? 0) === 1 ? '' : 's'}?`}
        message={`Your notifications panel will be emptied. This only clears them for you — anyone who has not cleared them still sees their own. Nothing is deleted: the emails, tasks and timeline entries behind these alerts are untouched.`}
        confirmLabel={clearing ? 'Clearing…' : `Clear ${notifData?.total ?? 0}`}
        tone="danger"
        onConfirm={async () => {
          if (clearing) return;
          setClearing(true);
          try {
            const r = await api.delete('/notifications');
            const n = r.data?.data?.dismissed ?? 0;
            setConfirmClearAll(false);
            showToast(n === 0 ? 'Nothing to clear' : `${n} notification${n === 1 ? '' : 's'} cleared`, 'success');
          } catch (err: any) {
            showToast(err?.response?.data?.error?.message ?? 'Could not clear them', 'error');
          } finally {
            setClearing(false);
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
            queryClient.invalidateQueries({ queryKey: ['notifications-page'] });
          }
        }}
        onCancel={() => { if (!clearing) setConfirmClearAll(false); }}
      />

      {/* User chip + logout */}
      <Box sx={{ p: '12px', borderTop: '1px solid #00263d', display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1, p: '6px 8px', borderRadius: '6px', bgcolor: '#00263d',
        }}>
          <Box sx={{
            width: 26, height: 26, borderRadius: '50%', bgcolor: user?.colour ?? '#61A0AF',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '11px', fontWeight: 700, color: '#fff', flexShrink: 0,
          }}>
            {user?.initials?.[0] ?? 'L'}
          </Box>
          <Typography sx={{ fontSize: '12px', color: '#d1d5db', fontWeight: 500, flex: 1 }}>
            {user?.name?.split(' ')[0] ?? 'User'}{user?.role === 'admin' ? ' · Admin' : ''}
          </Typography>
          <Box component="button" onClick={async () => {
            await axios.post('/api/auth/logout', {}, { withCredentials: true });
            window.location.href = '/login';
          }} sx={{
            bgcolor: 'transparent', border: 'none', color: '#a8c8d0', cursor: 'pointer',
            fontSize: '11px', fontFamily: 'inherit', p: '2px 6px', borderRadius: '4px',
            '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
          }}>
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
          </Box>
        </Box>
      </Box>
    </Box>
    </>
  );
}

function NavSection({ label }: { label: string }) {
  return (
    <Typography sx={{
      p: '12px 10px 4px', fontSize: '10px', fontWeight: 700, color: '#61A0AF',
      letterSpacing: '0.1em', textTransform: 'uppercase',
    }}>
      {label}
    </Typography>
  );
}

function NavItemRow({ label, icon, active, onClick }: { label: string; icon: React.ReactNode; active: boolean; onClick: () => void }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex', alignItems: 'center', gap: '9px',
        p: '8px 10px', m: '1px 6px', borderRadius: '6px', cursor: 'pointer',
        color: active ? '#fff' : '#a8c8d0', fontSize: '13px',
        bgcolor: active ? '#00263d' : 'transparent',
        transition: 'background 0.15s',
        '&:hover': { bgcolor: '#00263d', color: '#fff' },
      }}
    >
      {icon}
      {label}
    </Box>
  );
}

function NavDotRow({ label, colour, active, onClick }: { label: string; colour: string; active: boolean; onClick: () => void }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex', alignItems: 'center', gap: '9px',
        p: '8px 10px', m: '1px 6px', borderRadius: '6px', cursor: 'pointer',
        color: active ? '#fff' : '#a8c8d0', fontSize: '13px',
        bgcolor: active ? '#00263d' : 'transparent',
        transition: 'background 0.15s',
        '&:hover': { bgcolor: '#00263d', color: '#fff' },
      }}
    >
      <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: colour, flexShrink: 0 }} />
      {label}
    </Box>
  );
}
